import { inject, signal, effect } from "@angular/core";
import { LicResponse } from "../models/order/lic/lic.model";
import { SamlResponse } from "../models/order/saml/saml.model";
import { OrderResumeStateService } from "../store/state/lic/order-resume-state-service";
import { LoginResp, UserWelcomeResponse } from "../models/Login";
import { StepStatusType } from "../models/order/order.model";
import { ServiceInfoCollectionData } from "../models/order/workflow/service-info-collection.model";
import { PreOrderDetails, StartOrderDetails } from "../models/OrderDetails";
import { AppStateService } from "../store/state/app/app.state.service";
import { LoginFlowStateService } from "../store/state/login-flow/login-flow.state.service";
import { OrderStateServiceWrapper } from "../store/state/order/order.state.wrapper.service";
import { OrderStateServiceV2 } from "../store/state/order/orderv2/order-state.service";
import { OrderSetupStateService } from "../store/state/order/orderv2/workflows/order-setup/order-setup-state.service";
import { ServiceInformationCollectionStateService } from "../store/state/order/orderv2/workflows/service-info-collection/service-info-collection-state.service";
import { EmailValidationStateService } from "../store/state/order/orderv2/workflows/video/email-validation/email-validation.state.service";
import { UserStateService } from "../store/state/user/user.state.service";
import { AnalyticsService } from "./analytics/analytics.service";
import { AuthService } from "./auth.service";
import { DeviceService } from "./device.service";
import { OrderService } from "./order/order.service";
import { ADDRESS_CONSTANTS } from "../constants/address-constants";
import { BUSINESS_SEGMENT_OPTIONS, BUSINESS_TYPE_OPTIONS, ACCOUNT_TYPE_OPTIONS, EVO_OPTIONS, BUSINESS_INTERNET_PROVIDER_OPTIONS, BUSINESS_INTERNET_SPEED_OPTIONS } from "../constants/order-resume.constants";
import { OrderResumeCustomerResponse } from "../models/order/resume/order-resume.model";
import { SentryService } from "./sentry.service";

export abstract class OrderResumeService {

  orderStateServiceV2 = inject(OrderStateServiceV2)
  orderService = inject(OrderService)
  orderStateService = inject(OrderStateServiceWrapper)
  orderSetupStateService = inject(OrderSetupStateService)
  authService = inject(AuthService)
  loginFlowStateService = inject(LoginFlowStateService)
  userStateService = inject(UserStateService)
  analyticsService = inject(AnalyticsService);
  appStateService = inject(AppStateService);
  deviceService = inject(DeviceService);
  serviceInformationCollectionStateService = inject(ServiceInformationCollectionStateService)
    emailValidationStateService = inject(EmailValidationStateService)
  orderResumeStateService = inject(OrderResumeStateService)
  #sentryService = inject(SentryService)

  samlResponse= signal<SamlResponse>(null)
  licResponse= signal<LicResponse>(null)

  constructor() { 
    effect(() => {
      this.samlResponse.set(this.orderResumeStateService.getSamlPayload());
      this.licResponse.set(this.orderResumeStateService.getLicPayload());
    });
  }

  abstract processResponse(): void;


  protected processLogin(resp: LoginResp) {
      resp.UserDetails.SamlAuthenticated = true;
      const sessionId = this.appStateService.getSession()?.SessionID
      const authorized = !(resp.LoginFlow.Do2FaCheck || resp.LoginFlow.Do2FaSetup || resp.LoginFlow.DoForcedPasswordReset || resp.LoginFlow.DoSecurityQandA)
      this.deviceService.initDevice(resp.LoginFlow.LocationServicesOptional ?? false);
      this.loginFlowStateService.setLoginFlow(resp.LoginFlow)
      this.userStateService.setUser(resp.UserDetails)
      this.analyticsService.setUser(sessionId, resp);
      this.authService.setSentryUser(authorized)
  }

  protected processWelcome(resp: UserWelcomeResponse) {
      this.userStateService.setUserWelcome(resp)
      this.userStateService.setAuthorized(true)
      // A launch or SAML resume signs the user in without the login page, so load their feature toggles here too.
      this.authService.loadFeatureToggles().subscribe()
  }

  protected processPreOrder(preOrder: PreOrderDetails) {
      this.orderStateService.setPreOrder(preOrder);
      this.orderStateServiceV2.initOrder(preOrder.OrderNumber)
      this.orderStateServiceV2.updateOrder({ agentId: preOrder.AgentId });
      this.#sentryService.setTag('Order', preOrder.OrderNumber);
  }

  protected processStartOrder(startOrder: StartOrderDetails) {
      this.orderStateService.setStartOrder(startOrder);
  }

  protected processServiceInformationCollection(customerResponse: OrderResumeCustomerResponse) {
        const ispBan = customerResponse.IspBan?.trim() || null;
        this.emailValidationStateService.updateData({
            ispBan,
            ispBanProvidedByLaunch: !!ispBan
        });

    const serviceInfoData: ServiceInfoCollectionData = {
        businessInformation: {
            businessCanContact: 'Yes', //TODO
            businessName: customerResponse.BusinessName,
            phone: customerResponse.InstallPhone?.mobileFormat(),
            contactName: customerResponse.CustFullName,
            contactPhone: customerResponse.AltPhone?.mobileFormat(),
            firstName: customerResponse.CustFirstName,
            lastName: customerResponse.CustLastName,
            businessShareInternetProvider: true, //TODO
            businessIsExistingCustomer: false,
            businessInternetProvider: this.mapBusinessInternetProvider(customerResponse.BusinessInternetProvider),
            businessInternetSpeed: this.mapBusinessInternetSpeed(customerResponse.BusinessInternetSpeed),
            email: customerResponse.Email
        },
        billingAddress: {
            ...this.mapAddress(customerResponse.BillingAddress1, customerResponse.BillingAddress2),
            city: customerResponse.BillingCity,
            state: customerResponse.BillingState,
            zipCode: customerResponse.BillingZip,
            addressManuallyVerified: false,
        },
        customerInformation: {
            firstName: customerResponse.CustFirstName,
            lastName: customerResponse.CustLastName,
            email: customerResponse.Email,
            phone: customerResponse.InstallPhone?.mobileFormat(),
            alternatePhone: customerResponse.AltPhone?.mobileFormat(),
            canContact: "false",
            isExistingCustomer: 'No', //TODO
            isEmailVerifiedByCustomer: false,
            isPhoneVerifiedByCustomer: false,
            govtApproved: true,
            customerSelected: false,
            selectedCustomer: null,    //TODO
            customerCreditChecked: false
        },
        serviceAvailability: {
            ...this.mapAddress(customerResponse.CustAddress1, customerResponse.CustAddress2,
                customerResponse.CustUnitType, customerResponse.CustUnitNumber),
            city: customerResponse.CustCity,
            state: customerResponse.CustState,
            zipCode: customerResponse.CustZip,
            addressManuallyVerified: false
        },
        commercialAccount: {
            businessSegment: this.mapBusinessSegment(customerResponse.BusinessSegment),
            businessType: this.mapBusinessType(customerResponse.BusinessType),
            accountType: this.mapAccountType(customerResponse.AccountType),
            evo: this.mapEvo(customerResponse.BusinessEvo),
            fco: customerResponse.BusinessFco,
            canContactCommercial: false
        },
        businessInformationSMB: {
            businessNameSMB: '',
            businessTypeSMB: '',
            businessTaxIdSMB: '',
            businessTaxIdSMBRaw: '',
            confirmBusinessTaxIdSMB: ''
        },
        cpni: {},
        status: StepStatusType.InProgress
    }
    this.serviceInformationCollectionStateService.updateData(serviceInfoData);
    this.serviceInformationCollectionStateService.markStepComplete();
  }

      // Address forms use unit fields rather than a separate address-line-2 control.
  private mapAddress(line1: string, line2: string, unitType?: string, unitNumber?: string) {
      const secondary = line2?.trim();
      const unit = secondary?.match(/^(apartment|apt|suite|ste|unit|room|slip|lot|floor|fl|#)\.?\s*(\S+)$/i);
      const option = unit && ADDRESS_CONSTANTS.UNIT_TYPES.find(item =>
          item.label.toLowerCase() === unit[1].toLowerCase() || item.value?.toLowerCase() === unit[1].toLowerCase());
      if (option && (!unitNumber || unitNumber === unit[2])) {
          return { serviceAddress: line1, unitType: unitType || option.value, unitNumber: unitNumber || unit[2] };
      }
      return {
          serviceAddress: [line1, secondary].filter(Boolean).join(', '),
          unitType: unitType || null,
          unitNumber: unitNumber || null
      };
  }
  
  private mapBusinessSegment(businessSegment: string | null): string | null {
      const option = BUSINESS_SEGMENT_OPTIONS.find(opt => opt.label.toLowerCase() === businessSegment?.toLowerCase());
      return option ? option.value : businessSegment;
  }

  private mapBusinessType(businessType: string | null): string | null {
      const option = BUSINESS_TYPE_OPTIONS.find(opt => opt.label.toLowerCase() === businessType?.toLowerCase());
      return option ? option.value : businessType;
  }

  private mapAccountType(accountType: string | null): string | null {
      const option = ACCOUNT_TYPE_OPTIONS.find(opt => opt.label.toLowerCase() === accountType?.toLowerCase());
      return option ? option.value : accountType;
  }

  private mapEvo(evo: string | null): string | null {
      const option = EVO_OPTIONS.find(opt => opt.label.toLowerCase() === evo?.toLowerCase());
      return option ? option.value : evo;
  }

  private mapBusinessInternetProvider(provider: string | null): string | null {
      const option = BUSINESS_INTERNET_PROVIDER_OPTIONS.find(opt => opt.label.toLowerCase() === provider?.toLowerCase());
      return option ? option.value : provider;
  }

  private mapBusinessInternetSpeed(speed: string | null): string | null {
      const option = BUSINESS_INTERNET_SPEED_OPTIONS.find(opt => opt.label.toLowerCase() === speed?.toLowerCase());
      return option ? option.value : speed;
  }

  getResponse() {
    
  }
  
}
